// @flow
import type {Element} from 'react'
import React, {Component} from 'react'
import {addMonths, startOfToday, startOfTomorrow, format, isValid, parse} from 'date-fns'
import DatePicker from 'components/ui/DatePicker'
import FormGroup from 'components/ui/FormGroup'
import Value from 'components/proposals/Part/ui/Value'
import type {Alert} from 'components/types'
import type {Path, UpdateCallback} from 'components/proposals/types'
import flash from 'utils/MiscUtils/flash'
import type {Alert as ChangeAlert} from '../../types'

type Props = {
  alert: ?ChangeAlert,
  date: Value,
  isReadonly: boolean,
  onChange: UpdateCallback,
  path: Path,
}

const DATE_FORMAT = 'yyyy-MM-dd'

class SelectDate extends Component<Props> {
  static defaultProps: { alert: null } = {
    alert: null,
  }

  onChange: ((date: ?Date) => void) = (date: ?Date) => {
    const dateValue = date ? this.formatDate(date) : ''
    this.props.date.setValue(dateValue)
    this.props.onChange(this.props.path, this.props.date)
  }

  // eslint-disable-next-line class-methods-use-this
  formatDate: ((date: Date) => string) = (date: Date) => {
    const formattedDateString = format(date, DATE_FORMAT)
    const parsedDate = parse(formattedDateString, DATE_FORMAT, new Date())

    // The only sane way to check that date is valid is through formatting and parsing date object;
    // `parse` will return `Invalid Date` on bad input while vanilla `Date` will still convert it.
    if (isValid(parsedDate))
      return formattedDateString

    flash.danger(I18n.t('errors.messages.role_election_invalid_date'), {unique: true})
    return ''
  }

  setToMonthsFromNow: ((months: number) => void) = (months: number) => {
    const date = addMonths(startOfToday(), months)

    this.onChange(date)
  }

  // eslint-disable-next-line class-methods-use-this
  get minDate(): Date {
    return startOfTomorrow()
  }

  get formAlert(): ?Alert {
    return !this.props.isReadonly && !this.selectedDate ? this.props.alert : null
  }

  get selectedDate(): ?Date {
    return this.props.date.nextValue
      ? this.props.date.toValidLocalDate()
      : null
  }

  renderPreset(months: number, text: string): Element<"button"> {
    return (
      <button
        className="part-election-date-preset-button btn btn-sm btn-outline-primary"
        type="button"
        onClick={() => { this.setToMonthsFromNow(months) }}
      >
        {text}
      </button>
    )
  }

  // eslint-disable-next-line react/no-arrow-function-lifecycle
  render: (() => Element<"div">) = () => (
    <div>
      <FormGroup
        alert={this.formAlert}
        className="select-date"
        label={I18n.t('proposals.role_elect.until')}
        target="elect-role-filler-until"
      >
        <DatePicker
          className="form-control mb-2"
          disabled={this.props.isReadonly}
          id="elect-role-filler-until"
          minDate={this.minDate}
          onChange={this.onChange}
          selected={this.selectedDate}
        />
      </FormGroup>
      {!this.props.isReadonly && (
        <div>
          {this.renderPreset(6, `6 ${I18n.t('proposals.role_elect.months')}`)}
          {this.renderPreset(12, `1 ${I18n.t('proposals.role_elect.year', {count: 1})}`)}
          {this.renderPreset(24, `2 ${I18n.t('proposals.role_elect.year', {count: 2})}`)}
        </div>
      )}
    </div>
  )
}

export default SelectDate
